import { createApp } from 'vue';
import { createPinia } from 'pinia';
import * as Sentry from '@sentry/vue';
import posthog from 'posthog-js';
import App from './App.vue';
import router from './router';
import { useProgressStore } from './stores/progressStore';
import { useUserStore } from './stores/userStore';
import { authReady } from './services/authService';
import { usePlanLimits } from './composables/usePlanLimits';
// import { mathjax } from 'mathjax-full/js/mathjax.js';
// import { TeX } from 'mathjax-full/js/input/tex.js';
// import { SVG } from 'mathjax-full/js/output/svg.js';
// import { liteAdaptor } from 'mathjax-full/js/adaptors/liteAdaptor.js';
// import { RegisterHTMLHandler } from 'mathjax-full/js/handlers/html.js';

// Global styles (Tailwind)
import './assets/main.css';
import './assets/theme.css';
import './assets/ui.css';

// ── PostHog ───────────────────────────────────────────────────────────────────
// Initialise before the app mounts so the first pageview is captured.
// Opt out immediately if the user has not consented to analytics cookies.
// The CookieBanner writes 'all' or 'essential' to localStorage under this key.
const _cookieConsent = localStorage.getItem('cookie_consent');
const _analyticsAllowed = _cookieConsent === 'all';

posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
  api_host: import.meta.env.VITE_POSTHOG_HOST,
  capture_pageview: false,
  capture_pageleave: true,
  person_profiles: 'always',
  loaded(ph) {
    if (import.meta.env.DEV || !_analyticsAllowed) ph.opt_out_capturing();
  },
});

const app = createApp(App);

Sentry.init({
  app,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_ENV ?? (import.meta.env.PROD ? 'production' : 'development'),
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
  enabled: import.meta.env.PROD,
});

// const adaptor = liteAdaptor();
// RegisterHTMLHandler(adaptor);

// Install Pinia Stores
const pinia = createPinia();
app.use(pinia);

// Install Router
app.use(router);

// Track pageviews on each navigation using the route name, not the raw URL.
// This fires after the router resolves so the route name is always available.
router.afterEach((to) => {
  posthog.capture('$pageview', { $current_url: to.fullPath });
});

// Wait for Firebase to confirm auth state before mounting.
// This ensures the router guard always has a valid session on first navigation.
const userStore = useUserStore();
await authReady;
userStore.initFromStorage();

// Identify the user to PostHog on boot if already logged in (e.g. page refresh).
// Without this, events from existing sessions are attributed to anonymous persons.
if (userStore.session?.uid) {
  posthog.identify(userStore.session.uid, {
    email: userStore.session.email,
    name:  userStore.session.name,
    plan:  userStore.session.plan,
  });
}

// Mount App
app.mount('#app');

// Non-blocking: warm the plan definitions cache immediately so PaymentPage,
// SubscriptionPage, and usePlanLimits callers never wait on first load.
// The module-level singleton in usePlanLimits means this single call covers
// all components — subsequent loadPlans() calls return synchronously.
usePlanLimits().loadPlans();

// ─────────────────────────────────────────────────────────────
// Offline Recovery: Flush pending writes when back online
// ─────────────────────────────────────────────────────────────
const progressStore = useProgressStore();

// Initialize store (load pending writes from localStorage)
progressStore.init();

// Flush pending writes when coming back online
window.addEventListener('online', () => {
  progressStore.flushPendingWrites();
});

// Also try to flush when page becomes visible again (tab switch)
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && navigator.onLine) {
    progressStore.flushPendingWrites();
  }
});

// window.MathJax = {
//   tex: new TeX({ inlineMath: [['\\(', '\\)']] }),
//   svg: new SVG({ fontCache: 'global' }),
// };